import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { ALL_ROWS, rowWindowFor, sameRowWindow, type RowWindow } from './dynamicSelectUtils'

/**
 * A row the window has skipped: still in the DOM, taking up no space. Keeping it
 * there is what lets Autocomplete land keyboard highlight on rows the window has
 * passed over — returning nothing for them makes MUI skip the row entirely, and
 * the rest of a long list can never be reached from the keyboard.
 */
export const collapsedRowStyle: CSSProperties = {
  height: 0,
  minHeight: 0,
  padding: 0,
  overflow: 'hidden',
}

export type RowWindowing = {
  /** Whether the row renders in full, or as a collapsed stand-in. */
  isRendered: (row: number) => boolean
  /**
   * The skipped rows still have to take up their space, or the list would be
   * only as tall as the window and the scrollbar would jump around. The rows at
   * each edge of the window carry it; every other row gets nothing.
   */
  spacingOf: (row: number) => CSSProperties
  /** Note the scrolling list and pick the rows worth rendering at its position. */
  retarget: (listbox: HTMLElement) => void
  /** Back to the top of a whole list, for a popup reopening or a new search. */
  reset: () => void
  /** Scroll a row into the window; keyboard highlight can land outside it. */
  reveal: (row: number) => void
}

/**
 * Renders only the part of a long option list that is near the scroll position.
 * Shared by the filter and form pickers that page a list into an Autocomplete
 * listbox: without it every row that has ever loaded stays mounted, and scrolling
 * a few pages in turns into a stutter.
 *
 * Short lists are rendered in full — see `rowWindowFor` — so this costs nothing
 * until a list is long enough to be worth windowing.
 */
export const useRowWindow = (rowCount: number): RowWindowing => {
  const [rows, setRows] = useState<RowWindow>(ALL_ROWS)
  const rowHeight = useRef<number>(0)
  // Taken from the scroll event rather than a ref on the element, to leave the
  // ref Autocomplete puts on the listbox alone.
  const listbox = useRef<HTMLElement | null>(null)

  // Reading a row's height forces layout, so it is measured once and kept. A
  // list that cannot be measured — jsdom, or before the first paint — reports 0
  // and renders in full.
  const measureRow = (element: HTMLElement): number => {
    if (rowHeight.current === 0) {
      rowHeight.current = element.querySelector<HTMLElement>('[role="option"]')?.offsetHeight ?? 0
    }
    return rowHeight.current
  }

  const retargetFrom = useCallback(
    (element: HTMLElement) => {
      const next = rowWindowFor({
        scrollTop: element.scrollTop,
        clientHeight: element.clientHeight,
        rowHeight: measureRow(element),
        rowCount,
      })
      setRows(current => (sameRowWindow(current, next) ? current : next))
    },
    [rowCount],
  )

  const retarget = useCallback(
    (element: HTMLElement) => {
      listbox.current = element
      retargetFrom(element)
    },
    [retargetFrom],
  )

  // A page arriving can be what takes the list past the length worth windowing,
  // so the window is retargeted on growth as well as on scroll.
  useEffect(() => {
    if (listbox.current) retargetFrom(listbox.current)
  }, [retargetFrom])

  const reset = useCallback(() => {
    setRows(ALL_ROWS)
    if (listbox.current) listbox.current.scrollTop = 0
  }, [])

  const isRendered = (row: number) => row >= rows.start && row <= rows.end

  const spacingOf = (row: number): CSSProperties => {
    const lastRow = Math.min(rows.end, rowCount - 1)
    return {
      marginTop: row === rows.start ? row * rowHeight.current : undefined,
      marginBottom: row === lastRow ? (rowCount - 1 - row) * rowHeight.current : undefined,
    }
  }

  const reveal = useCallback(
    (row: number) => {
      if (row < 0 || (row >= rows.start && row <= rows.end)) return
      const element = listbox.current
      if (!element || rowHeight.current <= 0) return

      element.scrollTop = row * rowHeight.current
      retargetFrom(element)
    },
    [rows.start, rows.end, retargetFrom],
  )

  return { isRendered, spacingOf, retarget, reset, reveal }
}
